import type { RenderContext, SectionProps } from "../context";
import { field } from "../context";
import { Icon } from "../icons";
import { Container, EditorImagePlaceholder, Eyebrow, Img, MultiLine, REVEAL, STAGGER, SectionShell, SectionTitle, cx } from "../ui";

function Highlights({ ctx, items, center }: { ctx: RenderContext; items: string[] | undefined; center?: boolean }) {
  if (!items?.length) return null;
  return (
    <ul className={cx(STAGGER, "mt-8 flex flex-wrap gap-2.5", center && "justify-center")}>
      {items.map((h, i) => (
        <li
          key={i}
          className="inline-flex min-h-10 items-center gap-2 rounded-full border border-site-border bg-site-surface px-4 py-2 text-sm font-medium text-site-text"
          {...field(ctx, `content.highlights.${i}`)}
        >
          <span className="grid size-5 place-items-center rounded-full bg-site-primary text-site-on-primary">
            <Icon name="check" className="size-3" />
          </span>
          {h}
        </li>
      ))}
    </ul>
  );
}

export function About({ node, ctx }: SectionProps<"about">) {
  const c = node.content;
  const bodyClass = "mt-6 text-base leading-relaxed text-site-muted @3xl:text-lg";

  if (node.variant === "centered") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container size="narrow" className={cx(REVEAL, "text-center")}>
          <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="mb-3" />
          <SectionTitle ctx={ctx} node={node} text={c.title} className="text-balance" />
          <MultiLine ctx={ctx} text={c.body} path="content.body" className={cx(bodyClass, "mx-auto max-w-2xl")} />
          <Highlights ctx={ctx} items={c.highlights} center />
        </Container>
        {c.image ? (
          <Container className="mt-12 @3xl:mt-16">
            <div className="baze-reveal-pop aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-md @3xl:aspect-[21/9]">
              <Img ctx={ctx} image={c.image} sizes="(min-width: 1200px) 1120px, 100vw" path="content.image" target={1600} />
            </div>
          </Container>
        ) : null}
      </SectionShell>
    );
  }

  if (node.variant === "feature") return <Feature node={node} ctx={ctx} />;

  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <div className={cx("grid items-center gap-10 @4xl:gap-16", hasImage && "@4xl:grid-cols-2")}>
          {hasImage ? (
            <div className="baze-reveal-right aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-md @4xl:aspect-[5/6]">
              {c.image ? (
                <Img ctx={ctx} image={c.image} sizes="(min-width: 1024px) 540px, 100vw" path="content.image" />
              ) : (
                <EditorImagePlaceholder ctx={ctx} path="content.image" />
              )}
            </div>
          ) : null}
          <div className={cx(REVEAL, !hasImage && "max-w-3xl")}>
            <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="mb-3" />
            <SectionTitle ctx={ctx} node={node} text={c.title} className="text-balance" />
            <MultiLine ctx={ctx} text={c.body} path="content.body" className={bodyClass} />
            <Highlights ctx={ctx} items={c.highlights} />
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/** Story beside a framed photo with a floating highlights card; extra highlights become a numbered list. */
function Feature({ node, ctx }: SectionProps<"about">) {
  const c = node.content;
  const highlights = c.highlights ?? [];
  const floating = highlights.slice(0, 3);
  const listed = highlights.slice(3);
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="overflow-hidden">
      <Container>
        <div className={cx("grid items-center gap-14 @4xl:gap-20", hasImage && "@4xl:grid-cols-[1fr_1.05fr]")}>
          <div className={cx(REVEAL, !hasImage && "mx-auto max-w-3xl")}>
            <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="mb-3" />
            <SectionTitle ctx={ctx} node={node} text={c.title} className="text-balance" />
            <span aria-hidden="true" className="baze-draw-x mt-6 block h-1 w-16 rounded-full bg-site-primary" />
            <MultiLine ctx={ctx} text={c.body} path="content.body" className="mt-6 text-base leading-relaxed text-site-muted @3xl:text-lg" />
            {(hasImage ? listed : highlights).length > 0 ? (
              <ol className={cx(STAGGER, "mt-8 grid gap-4 @xl:grid-cols-2")}>
                {(hasImage ? listed : highlights).map((h, j) => {
                  const i = hasImage ? j + 3 : j;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span aria-hidden="true" className="baze-heading text-2xl leading-none text-site-primary tabular-nums">
                        {String(j + 1).padStart(2, "0")}
                      </span>
                      <span className="pt-0.5 font-medium text-site-text" {...field(ctx, `content.highlights.${i}`)}>
                        {h}
                      </span>
                    </li>
                  );
                })}
              </ol>
            ) : null}
          </div>
          {hasImage ? (
            <div className="baze-reveal-left relative pb-10 @4xl:pb-0">
              <div aria-hidden="true" className="baze-dots absolute -top-6 -right-6 size-40 text-site-primary opacity-60" />
              <div className="relative aspect-[4/5] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg">
                {c.image ? (
                  <Img ctx={ctx} image={c.image} sizes="(min-width: 1024px) 560px, 100vw" path="content.image" />
                ) : (
                  <EditorImagePlaceholder ctx={ctx} path="content.image" />
                )}
              </div>
              {floating.length > 0 ? (
                <ul className="baze-float absolute right-4 -bottom-2 left-4 rounded-site-md border border-site-border bg-site-bg/95 p-5 shadow-site-lg backdrop-blur @md:right-auto @md:max-w-xs @4xl:-bottom-8 @4xl:-left-10">
                  {floating.map((h, i) => (
                    <li key={i} className="flex items-center gap-3 py-1.5 text-sm font-medium text-site-text" {...field(ctx, `content.highlights.${i}`)}>
                      <span className="grid size-6 shrink-0 place-items-center rounded-full bg-site-primary text-site-on-primary">
                        <Icon name="check" className="size-3.5" />
                      </span>
                      {h}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      </Container>
    </SectionShell>
  );
}
